import { useCallback, useEffect, useReducer, useRef } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'

// Health state reducer for efficient state management
const healthReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null }
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        healthStatus: action.payload.status,
        healthDetails: action.payload.details,
        error: null,
      }
    case 'FETCH_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
        healthStatus: 'unknown',
        healthDetails: {},
      }
    case 'RESET':
      return { healthStatus: 'unknown', healthDetails: {}, loading: false, error: null }
    default:
      return state
  }
}

const initialState = {
  healthStatus: 'unknown',
  healthDetails: {},
  loading: true,
  error: null,
}

// Fault categorization for reusability
const categorizeFault = (fault) => {
  // Include device/source to distinguish between different fault sources
  const source = fault.device || fault.source || 'unknown'
  const description = `${source} ${fault.measurement}: ${fault.value}`
  const isCritical = /volt|temp/i.test(fault.measurement)
  return { description, isCritical }
}

/**
 * Device Health Analysis Hook
 * Analyzes current faults to determine device health status using Firebase functions
 * Simple and direct approach: no faults = healthy, faults = warning/critical
 * Uses real production data only
 */
export function useDeviceHealth(deviceId) {
  const [state, dispatch] = useReducer(healthReducer, initialState)
  const { globalPollingPaused } = useCloudContext()
  const intervalRef = useRef(null)
  const getCurrentFaultsRef = useRef(httpsCallable(functions, 'getCurrentFaults'))

  /**
   * Fetch device health based on current faults
   */
  const fetchDeviceHealth = useCallback(async () => {
    if (!deviceId) {
      dispatch({ type: 'RESET' })
      return
    }

    dispatch({ type: 'FETCH_START' })

    try {
      const result = await getCurrentFaultsRef.current({ serialNumber: deviceId })

      if (!result.data?.success) {
        throw new Error(result.data?.message ?? 'Failed to fetch device faults')
      }

      const deviceFaults = result.data.faults?.filter(fault => 
        fault.deviceId === deviceId || fault.serialNumber === deviceId,
      ) ?? []

      // Determine health status - count all fault records, not just unique descriptions
      const healthData = deviceFaults.reduce(
        (acc, fault) => {
          const { description, isCritical } = categorizeFault(fault)
          
          if (isCritical) {
            acc.issues.push(description)
            acc.status = 'critical'
          } else {
            acc.warnings.push(description)
            if (acc.status === 'healthy') acc.status = 'warning'
          }
          acc.totalFaults++
          return acc
        },
        { status: 'healthy', issues: [], warnings: [], totalFaults: 0 },
      )

      dispatch({
        type: 'FETCH_SUCCESS',
        payload: {
          status: healthData.status,
          details: {
            issues: healthData.issues,
            warnings: healthData.warnings,
            faultCount: healthData.totalFaults,
          },
        },
      })
    } catch (error) {
      console.error('Failed to fetch device health:', error)
      dispatch({ type: 'FETCH_ERROR', payload: error.message })
    }
  }, [deviceId])

  // Setup auto-refresh and initial fetch
  useEffect(() => {
    fetchDeviceHealth()

    if (deviceId && !globalPollingPaused) {
      intervalRef.current = setInterval(fetchDeviceHealth, 30000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [deviceId, fetchDeviceHealth, globalPollingPaused])

  return {
    ...state,
    refresh: fetchDeviceHealth,
  }
}
